import React from "react";
import { OpenConfirmationCallback, FlashMessageCallback, RegisterBeforeUnloadCallback, OpenDialogCallback } from 'spry-react/App/app';
import { FetchWithSerialOperationHandling } from 'spry-react/utils/fetchWithSerialOperationHandling';
import { CommandFetcher } from './commandFetcher';
import { ApiClient, SerialOperationApiClient } from 'spry-react/utils/apiClient';
import { SetWindowLocationHrefCallback } from 'spry-react/SerialOperation/setWindowLocationHref';
import { StartSerialOperationCallback } from 'spry-react/SerialOperation/serialOperation';
import { CrossSiteRequestForgeryTokenInfo } from 'spry-react/utils/fetchExtensions';
import { PortalSiteLinksDTO } from '../Api/GeneratedDTOs/ViewModels/Shared/PortalSiteLinksDTO';
import { LoggedInUser } from "Components/Api/GeneratedDTOs/ViewModels/Shared/LoggedInUser";

export interface IAppContext {
  fetcher: FetchWithSerialOperationHandling;
  commandFetcher: CommandFetcher;
  serialOperationApiClient: SerialOperationApiClient;
  standardApiClient: ApiClient;
  csrfOnlyApiClient: ApiClient;
  loaderApiClient: ApiClient;
  openDialog: OpenDialogCallback;
  confirm: OpenConfirmationCallback;
  flashMessage: FlashMessageCallback;
  registerBeforeUnload: RegisterBeforeUnloadCallback;
  setWindowLocationHref: SetWindowLocationHrefCallback;
  startSerialOperation: StartSerialOperationCallback;
}

export interface IPortalAppContext extends IAppContext {
  homeUrl: string;
  loggedInUser: LoggedInUser | null;
  timeTravelDate?: string | null;
  portalSiteLinks: PortalSiteLinksDTO;
  enableNotificationChecking: boolean;
  getCsrfInfo: () => CrossSiteRequestForgeryTokenInfo;
  logoColor: string;
  logoLabel: string;
  noRouter: boolean;
}

export const PortalAppContext = React.createContext<React.MutableRefObject<IPortalAppContext | null> | null>(null)

/**
 * Gets the PortalAppContext from the React context.
 */
export function usePortalAppContext(): IPortalAppContext {
  const contextRef = React.useContext(PortalAppContext);
  if (!contextRef) {
    throw new Error("usePortalAppContext must be used within a PortalAppContext.Provider");
  }
  if (!contextRef.current) {
    throw new Error("The PortalAppContext.Provider value has not been initialized with an IPortalAppContext instance");
  }
  return contextRef.current;
}
/** Gets the serialOperationApiClient from react context. */
export function useSerialOperationApiClient(): SerialOperationApiClient {
  return usePortalAppContext().serialOperationApiClient;
}
/** Gets the standardApiClient from react context. */
export function useStandardApiClient(): ApiClient {
  return usePortalAppContext().standardApiClient;
}
/** Gets the csrfOnlyApiClient from react context. */
export function useCsrfOnlyApiClient(): ApiClient {
  return usePortalAppContext().csrfOnlyApiClient;
}
/** Gets the loaderApiClient from react context. */
export function useLoaderApiClient(): ApiClient {
  return usePortalAppContext().loaderApiClient;
}